import React, { useMemo, useState } from "react";

function capitalize(str) {
  if (!str) return "";
  // Replace dashes with spaces, then capitalize
  return str.replace(/-/g, ' ').charAt(0).toUpperCase() + str.replace(/-/g, ' ').slice(1);
}

export default function ChatTabs({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  wsStatus,
  allDealers,
  onAddDealer,
  activeRfq,
  outstandingRfqs,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState("");

  // Use outstanding RFQs only (memoized so identity is stable for useMemo deps)
  const rfqSource = useMemo(() => outstandingRfqs || [], [outstandingRfqs]);
  
  const filteredRfqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rfqSource;
    
    return rfqSource.filter(
      (rfq) =>
        (rfq.dealerName || "").toLowerCase().includes(q) ||
        (rfq.rfqNumber || "").toLowerCase().includes(q) ||
        (rfq.material || "").toLowerCase().includes(q) ||
        (rfq.subMaterial || "").toLowerCase().includes(q)
    );
  }, [rfqSource, query]);

  const handleAddDealer = (rfqItem) => {
    // rfqItem should be an RFQ object with rfqNumber and dealerName
    if (!rfqItem || !rfqItem.rfqNumber) {
      alert('Invalid RFQ selected.');
      return;
    }

    // Create a dealer-like object for the chat tab
    // Use dealerName as the company name (not "Unknown Dealer")
    const dealerObj = {
      id: rfqItem.dealerId || rfqItem.rfqNumber,
      name: rfqItem.dealerName || rfqItem.dealer || 'Company',
      location: '',
      material: rfqItem.material || '',
      subMaterial: rfqItem.subMaterial || '',
    };

    onAddDealer(dealerObj, rfqItem.rfqNumber);
    setShowPicker(false);
    setQuery("");
  };

  return (
    <>
      {/* Tabs row at the top with + tab */}
  <div className="px-2 pt-2 flex gap-1 overflow-x-auto">
        {tabs.map((t) => {
          const active = activeTabId === t.id;
          const status = wsStatus[t.dealerId] || "connecting";
          const statusColor =
            status === "open"
              ? "bg-green-500"
              : status === "connecting"
              ? "bg-yellow-500"
              : "bg-red-500";
          
          return (
            <div
              key={t.id}
              onClick={() => onTabClick(t.id)}
              className={[
                "relative h-8 px-4 flex items-center cursor-pointer select-none",
                "transform -skew-x-12 rounded-t border",
                active
                  ? "bg-gray-900 dark:bg-gray-950 text-white border-gray-700 dark:border-gray-800 mb-[-1px] border-b-0"
                  : "bg-gray-700 dark:bg-gray-800 text-gray-100 border-gray-600 dark:border-gray-700 hover:bg-gray-600 dark:hover:bg-gray-700 border-b-0",
              ].join(" ")}
              title={`${t.dealerLocation || ""} • ${status}`}
            >
              <span className={`w-2 h-2 rounded-full ${statusColor} mr-2 transform skew-x-12`} />
              <span className="transform skew-x-12 whitespace-nowrap">{t.dealerName || 'Company'}</span>
              <button
                className="ml-2 transform skew-x-12 text-xs rounded px-1 hover:bg-gray-600 dark:hover:bg-gray-700"
                onClick={(e) => { e.stopPropagation(); onTabClose(t.id); }}
                title="Close tab"
              >
                ×
              </button>
            </div>
          );
        })}
        <button
          onClick={() => setShowPicker((s) => !s)}
          disabled={!rfqSource || rfqSource.length === 0}
          title={rfqSource && rfqSource.length ? "Start a chat for an RFQ" : "No outstanding RFQs available"}
          className={[
            "relative h-8 px-3 flex items-center cursor-pointer select-none disabled:opacity-50",
            "transform -skew-x-12 rounded-t border",
            "bg-gray-600 dark:bg-gray-700 text-white border-gray-500 dark:border-gray-600 hover:bg-gray-500 dark:hover:bg-gray-600",
          ].join(" ")}
        >
          <span className="transform skew-x-12 text-lg leading-none">+</span>
        </button>
      </div>

      {/* RFQ picker */}
      {showPicker && (
        <div className="mx-4 mt-2 p-3 bg-gray-200 dark:bg-gray-700 border border-gray-400 dark:border-gray-600 rounded select-text">
          <div className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search RFQs..."
              className="flex-1 px-2 py-1 rounded border border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <button
              className="px-2 py-1 text-sm bg-gray-500 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-700 text-white rounded"
              onClick={() => setShowPicker(false)}
            >
              Close
            </button>
          </div>
          <div className="mt-2 max-h-40 overflow-auto">
            {(!filteredRfqs || filteredRfqs.length === 0) && (
              <div className="text-sm text-gray-700 dark:text-gray-300 px-1 py-2">
                No RFQs found.
              </div>
            )}
            {filteredRfqs.map((rfq) => (
              <button
                key={rfq.rfqNumber}
                className="w-full text-left px-2 py-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                onClick={() => handleAddDealer(rfq)}
                title={`${rfq.material || 'Material'} - ${rfq.rfqNumber}`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">{rfq.dealerName || rfq.dealer || 'Company'}</div>
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  RFQ: {rfq.rfqNumber}
                  {rfq.material && ` • ${capitalize(rfq.material)}`}{rfq.subMaterial ? ` (${capitalize(rfq.subMaterial)})` : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
